<template>
    <div class="template-dashboard">
        <!-- 로딩 오버레이 -->
        <div v-if="loading" class="loading-overlay">
            <div class="loading-spinner"></div>
        </div>

        <!-- 헤더 -->
        <TemplateHeader
            :title="title"
            :show-back="showDetail"
            :current-year="currentYear"
            :current-month="currentMonth"
            :show-date-picker="showDatePicker"
            @back="closeDetail"
            @date-navigate="handleDateNavigate"
            @year-change="handleYearChange"
            @month-select="handleMonthSelect"
            @date-picker-toggle="toggleDatePicker"
            @date-picker-close="closeDatePicker"
            @settings-open="openSettings"
        />

        <!-- 메인 대시보드 (전체 선택 시) -->
        <div v-if="!showDetail" class="dashboard-content">
            <!-- 상단: 요약 정보 카드 -->
            <TemplateSummaryCards>
                <slot name="summary-cards"></slot>
            </TemplateSummaryCards>

            <!-- 중간: 좌우 레이아웃 -->
            <div class="main-content-section">
                <!-- 좌측: 목록 -->
                <TemplateList @item-click="selectItem">
                    <slot name="list" :handleItemClick="selectItem"></slot>
                </TemplateList>

                <!-- 우측: 차트 영역 -->
                <TemplateChartsArea>
                    <slot name="charts"></slot>
                </TemplateChartsArea>
            </div>
        </div>

        <!-- 상세 화면 -->
        <div v-if="showDetail && selectedItem" class="detail-view">
            <!-- 상단: 1행 4열 레이아웃 -->
            <div class="top-section-layout">
                <TemplateDetailCards>
                    <slot name="detail-cards" :selectedItem="selectedItem"></slot>
                </TemplateDetailCards>
            </div>

            <!-- 차트 및 분석 섹션 -->
            <div class="analysis-section">
                <TemplateDetailTabs :tabs="detailTabs" :currentTab="currentDetailTab" @tab-change="handleTabChange">
                    <slot name="detail-tabs" :currentTab="currentDetailTab" :selectedItem="selectedItem"></slot>
                </TemplateDetailTabs>
            </div>
        </div>
    </div>
</template>

<script>
import TemplateHeader from "./components/TemplateHeader.vue";
import TemplateSummaryCards from "./components/TemplateSummaryCards.vue";
import TemplateList from "./components/TemplateList.vue";
import TemplateChartsArea from "./components/TemplateChartsArea.vue";
import TemplateDetailCards from "./components/TemplateDetailCards.vue";
import TemplateDetailTabs from "./components/TemplateDetailTabs.vue";

export default {
    name: "TemplateDashboard",
    components: {
        TemplateHeader,
        TemplateSummaryCards,
        TemplateList,
        TemplateChartsArea,
        TemplateDetailCards,
        TemplateDetailTabs,
    },
    props: {
        title: {
            type: String,
            default: "대시보드",
        },
        loading: {
            type: Boolean,
            default: false,
        },
        detailTabs: {
            type: Array,
            default: () => [
                { key: "tab1", label: "탭 1" },
                { key: "tab2", label: "탭 2" },
                { key: "tab3", label: "탭 3" },
            ],
        },
    },
    data() {
        return {
            showDetail: false,
            currentYear: new Date().getFullYear(),
            currentMonth: new Date().getMonth() + 1,
            showDatePicker: false,
            selectedItem: null,
            currentDetailTab: "tab1",
        };
    },
    methods: {
        selectItem(item) {
            this.selectedItem = item;
            this.showDetail = true;
            this.currentDetailTab = this.detailTabs[0]?.key || "tab1";
            this.$emit("item-select", item);
        },
        closeDetail() {
            this.showDetail = false;
            this.selectedItem = null;
            this.$emit("detail-close");
        },
        handleDateChange(year, month) {
            this.currentYear = year;
            this.currentMonth = month;
            this.$emit("date-change", { year, month });
        },
        handleDateNavigate(direction) {
            this.currentMonth += direction;
            if (this.currentMonth > 12) {
                this.currentMonth = 1;
                this.currentYear += 1;
            } else if (this.currentMonth < 1) {
                this.currentMonth = 12;
                this.currentYear -= 1;
            }
            this.$emit("date-change", { year: this.currentYear, month: this.currentMonth });
        },
        handleYearChange(direction) {
            this.currentYear += direction;
            this.$emit("date-change", { year: this.currentYear, month: this.currentMonth });
        },
        handleMonthSelect(month) {
            this.currentMonth = month;
            this.showDatePicker = false;
            this.$emit("date-change", { year: this.currentYear, month: this.currentMonth });
        },
        toggleDatePicker() {
            this.showDatePicker = !this.showDatePicker;
        },
        closeDatePicker() {
            this.showDatePicker = false;
        },
        handleTabChange(tabKey) {
            this.currentDetailTab = tabKey;
            this.$emit("tab-change", tabKey);
        },
        openSettings() {
            this.$emit("settings-open");
        },
    },
};
</script>

<style scoped>
/* 기본 컨테이너 */
.template-dashboard {
    color: #1e293b;
    height: 100%;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    background: #f8fafc;
}

/* 로딩 오버레이 */
.loading-overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(255, 255, 255, 0.8);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
    border-radius: 8px;
}

.loading-spinner {
    width: 40px;
    height: 40px;
    border: 4px solid #e2e8f0;
    border-top-color: #64748b;
    border-radius: 50%;
    animation: spin 1s linear infinite;
}

@keyframes spin {
    to {
        transform: rotate(360deg);
    }
}

/* 대시보드 컨텐츠 */
.dashboard-content {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 12px;
    min-height: 0;
    overflow: hidden;
}

/* 메인 콘텐츠 섹션 */
.main-content-section {
    display: grid;
    grid-template-columns: calc(25% - 9px) 1fr;
    gap: 12px;
    flex: 1;
    min-height: 0;
    overflow: hidden;
}

/* 상세 화면 */
.detail-view {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 12px;
    overflow-y: auto;
    min-height: 0;
}

/* 상단: 1행 4열 레이아웃 */
.top-section-layout {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 12px;
    flex-shrink: 0;
    max-height: 330px;
}

/* 분석 섹션 */
.analysis-section {
    flex: 1;
    min-height: 400px;
}
</style>
